/**
 * File: /Users/tomasmuniesa/Documents/develop/nousmedis/nm-apps/3_catalog_cloud/develop/frontend/src/app/modules/catalog-cloud/components/page/page-template-import-dialog/page-template-import-dialog.component.ts
 * Project: /Users/tomasmuniesa/Documents/develop/nousmedis/nm-apps/3_catalog_cloud/develop/frontend
 * Created Date: Wednesday, March 18th 2026, 4:05:27 pm
 * Author: Tomás
 * -----
 * Last Modified:  Wednesday, 18th March 2026 4:05:30 pm
 * Modified By: Tomás (tomas@cege.es>)
 * -----
 * Copyright (c) 2026 Nousmedis, CeGe
 * ------------------------------------
 */


/*
 * File: page-template-import-dialog.component.ts                             *
 * Project: catalog-cloud                                                      *
 * Author: Tomás Muniesa (tomas@cege.es)                                       *
 * Copyright 2021 - 2024 Nousmedis, CeGe                                       *
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthModel } from '@nousmedis/nm-guard-auth';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { PageTemplateService } from '../../../services/page-template.service';

@Component({
  selector: 'app-page-template-import-dialog',
  templateUrl: './page-template-import-dialog.component.html',
  styleUrls: ['./page-template-import-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageTemplateImportDialogComponent implements OnInit {
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() imported = new EventEmitter<any>();

  form!: FormGroup;
  templateData: any = null;
  fileName: string = '';
  loading: boolean = false;

  private readonly destroyRef = inject(DestroyRef);
  private readonly pageTemplateService = inject(PageTemplateService);
  private readonly auth = inject(AuthModel);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.form = new FormGroup({
      name: new FormControl<string>('', [Validators.required]),
    });
  }

  get nameControl(): FormControl<string> {
    return this.form.get('name') as FormControl<string>;
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    this.fileName = file.name;

    const reader = new FileReader();
    reader.onload = () => {
      try {
        this.templateData = JSON.parse(reader.result as string);
      } catch {
        this.templateData = null;
        this.fileName = '';
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('ERROR'),
          detail: this.translate.instant('PAGE.IMPORT_INVALID_JSON'),
        });
      }
      this.cdr.markForCheck();
    };
    reader.readAsText(file);
  }

  importTemplate(): void {
    if (this.form.invalid || !this.templateData) return;

    this.loading = true;
    this.cdr.markForCheck();

    const userId = this.auth.getCurrentUser().id;
    const name: string = this.nameControl.value;

    this.pageTemplateService
      .importTemplate(this.templateData, name, userId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: any) => {
          this.imported.emit(result);
          this.close();
        },
        error: () => {
          this.loading = false;
          this.cdr.markForCheck();
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('ERROR'),
            detail: this.translate.instant('PAGE.IMPORT_ERROR'),
          });
        },
      });
  }

  cancel(): void {
    this.close();
  }

  private close(): void {
    this.loading = false;
    this.templateData = null;
    this.fileName = '';
    this.form.reset();
    this.visible = false;
    this.visibleChange.emit(false);
    this.cdr.markForCheck();
  }
}
